import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { useImagesLoaded } from '../../../../hooks/useImagesLoaded';
import { COLOR_BLACK, COLOR_OAT_MILK, COLOR_STEAK_SAUCE } from '../../../colors';
import { FUTURABOOK } from '../../../fonts';
import { addCommas } from '../../../../utils';

const COPYRIGHT_YEAR = new Date().getFullYear();
const useStyles = makeStyles((theme)=> ({
    flexContainer: {
        display: 'flex',
        minHeight: '100%',
        position: 'relative',
        fontFamily: ['futurabook', 'Arial', 'Helvetica', 'sans-serif'],
        fontWeight: 500,
    },
    leftWrap: {
        flex: 1,
        boxSizing: 'border-box',
        padding: 36,
        minHeight: '100%',
        display: 'flex',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: COLOR_STEAK_SAUCE,
    },
    rightWrap: {
        flex: 1,
        boxSizing: 'border-box',
        padding: '24px 36px' ,
        minHeight: '100%',
        textAlign: 'left',
        fontSize: '28px',
        lineHeight: '36px',
        backgroundColor: COLOR_OAT_MILK,
        color: COLOR_BLACK,
    },
    complianceWrap: {
        position: 'absolute',
        width: 420,
        bottom: 16,
        right: 15,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    disclaimerWrap: {
        width: "100%",
        color: COLOR_STEAK_SAUCE,
        textAlign: 'left',
        fontSize: '10px',
        lineHeight: '12px',
        letterSpacing: '0.4px',
    },
    lrLogo: {
        height: '12px',
    },
    realtorLogo: {
        marginLeft: '10px',
        height: '24px',
    },
    equalHousingLogo: {
        marginLeft: '20px',
        height: '24px',
    },
    filmstrip: {
        position: 'absolute',
        bottom: 86,
        right: 36,
        display: 'flex',
        filter: 'drop-shadow(0px 0px 15px rgba(0, 0, 0, 0.15))',
    },
    filmstripCell: {
        flex: 1,
        border: '8px solid #fff',
        height: 160,
        width: 214,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: COLOR_STEAK_SAUCE,
    },
}));

export default function Filmstrip({
    templateContent = {},
    currentFrame = 1,
    onImagesLoaded = null,
}) {
    const classes = useStyles();
    const {
        hero,
        address,
        city,
        state,
        photos,
        bedrooms,
        bathrooms,
        home_size,
        price_amount,
        open_houses = [],
        listing_status_label,
        realtor_logo_required = false,
    } = templateContent || {};
    const heroImage = (photos && photos.length) ? photos[0] : hero;
    const sqft = parseInt((home_size || '').replace(/\D/g, ''), 10) > 0 ? parseInt(home_size.replace(/\D/g, ''), 10) : null;
    const openHouses = open_houses || [];
    const loadedBgs = useImagesLoaded((photos && photos.length) ? photos : [heroImage]);
    if(!heroImage) {
        window.isLoaded = true;
    }

    useEffect(() => {
        if(loadedBgs && !window.isLoaded) {
            if(typeof onImagesLoaded === 'function') {
                onImagesLoaded();
            } else {
                window.isLoaded = true;
            }
        }
        return ()=> {
            window.isLoaded = false;
        };
    }, [loadedBgs, onImagesLoaded]);

    const display_address = address?.length > 0;

    return (
        <>
            <link rel="stylesheet" href={FUTURABOOK} />

            <div className={classes.flexContainer}>

                {/* Left Block */}

                <div className={classes.leftWrap}
                    style={{
                        backgroundImage: `url("${heroImage}")`
                    }}
                >
                </div>


                {/* Right Block */}

                <div className={classes.rightWrap}>

                    <div style={{
                        fontSize: '52px',
                        lineHeight: '54px',
                        fontWeight: 900,
                        marginTop: openHouses.length ? 0 : 42,
                    }}>
                        {`${listing_status_label || 'Just Listed'}`}
                    </div>

                    {openHouses.length > 0 && (
                        openHouses.map((openHouse)=> {
                            return (
                                <div key={`${openHouse.date}${openHouse.time}`}>
                                    {openHouse.date} &bull; {openHouse.time}
                                </div>
                            );
                        })
                    )}

                    {openHouses.length > 0 && (
                        <div style={{
                            marginTop: 20,
                            marginBottom: 20,
                            width: 42,
                            height: 0,
                            borderTop: `4px solid ${COLOR_BLACK}`,
                        }}></div>
                    )}

                    {price_amount && (<div style={{
                        fontSize: '22px',
                        lineHeight: '32px',
                    }}>
                        {`$${addCommas(price_amount)}`}
                    </div>)}

                    <div style={{ fontSize: '22px', lineHeight: '32px' }}>
                        {bedrooms > 0 && (<span>{bedrooms} bd</span>)}{' '}
                        {bedrooms > 0 && (bathrooms > 0 || sqft) && (<span>&bull;</span>)}{' '}

                        {bathrooms > 0 && (<span>{bathrooms} ba</span>)}{' '}
                        {bathrooms > 0 && (sqft && (<span>&bull;</span>))}{' '}

                        {sqft && (<span>{addCommas(sqft)} sqft</span>)}{' '}
                    </div>

                    {!display_address && city && (
                        <div style={{ fontSize: '22px', lineHeight: '32px' }}>
                            in {city} {state}
                        </div>
                    )}

                    {display_address && (
                        <div style={{ fontSize: '22px', lineHeight: '32px' }}>
                            {address.map((addressLine)=> <div key={addressLine}>{addressLine}</div>)}  
                        </div>
                    )}
                    
                    <div className={classes.filmstrip}>
                        <div className={classes.filmstripCell} style={{
                            backgroundImage: (photos && photos[1]) ? `url('${photos[1]}')` : 'none',
                        }}></div>
                        <div className={classes.filmstripCell} style={{
                            borderLeft: 'none',
                            backgroundImage: (photos && photos[2]) ? `url('${photos[2]}')` : 'none',
                        }}></div>
                    </div>

                    {/* Compliance */}

                    <div className={classes.complianceWrap}>
                        <div className={classes.disclaimerWrap}>
                            <img src="./images/lr-logo-disclaimer-grey.svg" className={classes.lrLogo} alt="LR Logo" />
                            <div>
                                &copy; ListReports {COPYRIGHT_YEAR}, all rights reserved
                            </div>
                            <div>
                                This information is from third-party sources and its accuracy cannot be guaranteed
                            </div>
                        </div>
                        {realtor_logo_required && 
                            <img src="./images/realtor-logo-grey.svg" className={classes.realtorLogo} alt="Realtor logo" />
                        }
                        <img src="./images/equal-housing-logo-grey.svg" className={classes.equalHousingLogo} alt="Equal Housing logo" />
                    </div>

                </div>

            </div>
        </>
    );
}
